import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/Shared";
import ItemDialog from "@/components/ItemDialog";
import TransactionDialog from "@/components/TransactionDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, ArrowsLeftRight, Package, DownloadSimple, FilePdf, FileXls, HandArrowUp, Printer, Stack, EnvelopeSimple, Gear } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import CategoryManager from "@/components/CategoryManager";

const EMPTY_STATE_IMG = "https://images.pexels.com/photos/6129584/pexels-photo-6129584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Inventory() {
  const { user, money } = useAuth();
  const can = (p) => user?.permissions?.includes(p);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [itemDialog, setItemDialog] = useState({ open: false, item: null });
  const [txDialog, setTxDialog] = useState({ open: false, item: null, type: "usage" });
  const [toDelete, setToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchReport = async (fmt, categoryOverride) => {
    const params = {};
    const useCat = categoryOverride !== undefined ? categoryOverride : (cat !== "all" ? cat : undefined);
    if (useCat) params.category = useCat;
    if (statusFilter !== "all") params.status = statusFilter;
    const res = await api.get(`/reports/stock.${fmt}`, { params, responseType: "blob" });
    return { blob: res.data, label: useCat ? useCat.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "all" };
  };

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const exportReport = async (fmt, categoryOverride) => {
    setExporting(true);
    try {
      const { blob, label } = await fetchReport(fmt, categoryOverride);
      saveBlob(blob, `stock-${label}-${new Date().toISOString().slice(0, 10)}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  // Export every category as its own separate file
  const exportEachCategory = async (fmt) => {
    if (categories.length === 0) { toast.error("No categories to export"); return; }
    setExporting(true);
    try {
      for (const c of categories) {
        const { blob, label } = await fetchReport(fmt, c);
        saveBlob(blob, `stock-${label}-${new Date().toISOString().slice(0, 10)}.${fmt}`);
        await new Promise((r) => setTimeout(r, 600));
      }
      toast.success(`Exported ${categories.length} category files`);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  // Print the PDF report (respects current or chosen category) via a hidden iframe
  const printReport = async (categoryOverride) => {
    setExporting(true);
    try {
      const { blob } = await fetchReport("pdf", categoryOverride);
      const url = URL.createObjectURL(blob);
      const frame = document.createElement("iframe");
      frame.style.position = "fixed"; frame.style.right = "0"; frame.style.bottom = "0";
      frame.style.width = "0"; frame.style.height = "0"; frame.style.border = "0";
      frame.src = url;
      frame.onload = () => { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { window.open(url, "_blank"); } };
      document.body.appendChild(frame);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Print failed"); } finally { setExporting(false); }
  };

  const emailReport = async (period, categoryOverride) => {
    setExporting(true);
    try {
      const params = {};
      if (period) params.period = period;
      const useCat = categoryOverride !== undefined ? categoryOverride : (cat !== "all" ? cat : undefined);
      if (useCat) params.category = useCat;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.post("/reports/stock/email", null, { params });
      const pl = period ? period[0].toUpperCase() + period.slice(1) + " report" : "Report";
      toast.success(`${pl}${useCat ? ` — ${useCat}` : ""} emailed to ${data.sent_to}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Email failed"); }
    finally { setExporting(false); }
  };

  const load = async () => {
    const [inv, cats] = await Promise.all([api.get("/inventory"), api.get("/categories")]);
    setItems(inv.data);
    setCategories(cats.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (cat !== "all" && it.category !== cat) return false;
    if (statusFilter !== "all" && it.status !== statusFilter) return false;
    if (q && !`${it.name} ${it.sku} ${it.supplier}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, q, cat, statusFilter]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/inventory/${toDelete.id}`);
      toast.success("Item deleted");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <PageHeader title="Inventory" subtitle="P&O components, consumables, office items and company assets">
        {can("item:create") && <Button data-testid="add-item-button" onClick={() => setItemDialog({ open: true, item: null })} className="bg-primary hover:bg-primary/90 gap-2">
          <Plus size={17} weight="bold" /> Add item
        </Button>}
        {can("category:create") && <CategoryManager onChanged={load} />}
        {can("report:export") && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="export-report-btn" disabled={exporting}>
              <DownloadSimple size={17} weight="bold" /> {exporting ? "Working…" : "Export / Print"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>{cat === "all" ? "All categories" : cat}</DropdownMenuLabel>
            <DropdownMenuItem data-testid="export-pdf-item" onClick={() => exportReport("pdf")}><FilePdf size={16} /> Download PDF</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-xlsx-item" onClick={() => exportReport("xlsx")}><FileXls size={16} /> Download Excel</DropdownMenuItem>
            <DropdownMenuItem data-testid="print-item" onClick={() => printReport()}><Printer size={16} /> Print{cat !== "all" ? ` "${cat}"` : ""}</DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="email-report-sub" className="gap-2"><EnvelopeSimple size={16} /> Email report</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-60">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Sent to inventory-stock@raphadon.org</DropdownMenuLabel>
                <DropdownMenuItem data-testid="email-report-now" onClick={() => emailReport()}>Current snapshot{cat !== "all" ? ` — ${cat}` : ""}</DropdownMenuItem>
                <DropdownMenuItem data-testid="email-report-week" onClick={() => emailReport("week")}>Weekly report</DropdownMenuItem>
                <DropdownMenuItem data-testid="email-report-month" onClick={() => emailReport("month")}>Monthly report</DropdownMenuItem>
                <DropdownMenuItem data-testid="email-report-year" onClick={() => emailReport("year")}>Yearly report</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="email-by-category" className="gap-2"><Package size={16} /> Email a category</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                    {categories.length === 0 ? <DropdownMenuItem disabled>No categories</DropdownMenuItem> : categories.map((c) => (
                      <DropdownMenuSub key={c}>
                        <DropdownMenuSubTrigger data-testid={`email-cat-${c}`}>{c}</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem onClick={() => emailReport(null, c)}>Current snapshot</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => emailReport("week", c)}>Weekly</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => emailReport("month", c)}>Monthly</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => emailReport("year", c)}>Yearly</DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="export-by-category" className="gap-2"><Package size={16} /> Export a category</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                {categories.length === 0 ? <DropdownMenuItem disabled>No categories</DropdownMenuItem> : categories.map((c) => (
                  <DropdownMenuSub key={c}>
                    <DropdownMenuSubTrigger data-testid={`cat-export-${c}`}>{c}</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => exportReport("pdf", c)}><FilePdf size={16} /> PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportReport("xlsx", c)}><FileXls size={16} /> Excel</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => printReport(c)}><Printer size={16} /> Print</DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem data-testid="export-each-pdf" onClick={() => exportEachCategory("pdf")}><Stack size={16} /> Each category → separate PDFs</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-each-xlsx" onClick={() => exportEachCategory("xlsx")}><Stack size={16} /> Each category → separate Excels</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
      </PageHeader>

      <div className="p-6 md:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items, SKU, supplier…" className="pl-9" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="optimal">Optimal</SelectItem>
              <SelectItem value="low">Low stock</SelectItem>
              <SelectItem value="out">Out of stock</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading inventory…</div>
        ) : items.length === 0 ? (
          <div className="bg-card border border-border rounded-md overflow-hidden flex flex-col md:flex-row items-center">
            <img src={EMPTY_STATE_IMG} alt="Organizing supplies" className="w-full md:w-1/2 h-56 md:h-72 object-cover" />
            <div className="p-8 md:p-10">
              <h3 className="text-xl font-heading font-semibold">Your store is empty</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm">Add your first prosthetic or orthotic item to start tracking stock, usage and alerts.</p>
              {can("item:create") && <Button data-testid="empty-add-item" onClick={() => setItemDialog({ open: true, item: null })} className="bg-primary hover:bg-primary/90 gap-2 mt-5">
                <Plus size={17} weight="bold" /> Add your first item
              </Button>}
            </div>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inventory-table">
                <thead>
                  <tr className="border-b border-border bg-accent/60 text-left">
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Item</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Category</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">In stock</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">Min</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">Value</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((it) => (
                    <tr key={it.id} data-testid={`inventory-row-${it.id}`} className="hover:bg-accent/40 transition-colors duration-150">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {it.image_path ? (
                            <img src={`${API}/files/${it.image_path}`} alt={it.name} data-testid={`item-thumb-${it.id}`} className="h-10 w-10 rounded-md object-cover border border-border shrink-0" />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0"><Package size={18} className="text-muted-foreground" /></div>
                          )}
                          <div>
                            <p className="font-medium">{it.name}</p>
                            <p className="text-xs text-muted-foreground">{it.sku || "—"}{it.supplier ? ` · ${it.supplier}` : ""}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{it.category}</td>
                      <td className="px-4 py-3 text-right tnums font-medium">{it.quantity} <span className="text-muted-foreground font-normal">{it.unit}</span></td>
                      <td className="px-4 py-3 text-right tnums text-muted-foreground">{it.min_threshold}</td>
                      <td className="px-4 py-3 text-right tnums text-muted-foreground">{money(it.quantity * it.unit_price)}</td>
                      <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" data-testid={`take-out-${it.id}`} onClick={() => setTxDialog({ open: true, item: it, type: "issue" })} title="Take from store" disabled={it.quantity <= 0} className="h-8 w-8 hover:text-destructive">
                            <HandArrowUp size={16} />
                          </Button>
                          <Button size="icon" variant="ghost" data-testid={`record-tx-${it.id}`} onClick={() => setTxDialog({ open: true, item: it, type: "usage" })} title="Record movement" className="h-8 w-8 hover:text-primary">
                            <ArrowsLeftRight size={16} />
                          </Button>
                          {can("item:update") && <Button size="icon" variant="ghost" data-testid={`edit-item-${it.id}`} onClick={() => setItemDialog({ open: true, item: it })} title="Edit" className="h-8 w-8 hover:text-primary">
                            <PencilSimple size={16} />
                          </Button>}
                          {can("item:delete") && <Button size="icon" variant="ghost" data-testid={`delete-item-${it.id}`} onClick={() => setToDelete(it)} title="Delete" className="h-8 w-8 hover:text-destructive">
                            <Trash size={16} />
                          </Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                      <Package size={28} className="mx-auto mb-2 opacity-40" /> No items match your filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ItemDialog open={itemDialog.open} onOpenChange={(o) => setItemDialog((s) => ({ ...s, open: o }))} item={itemDialog.item} categories={categories} onSaved={load} onCategoryAdded={load} />
      <TransactionDialog open={txDialog.open} onOpenChange={(o) => setTxDialog((s) => ({ ...s, open: o }))} item={txDialog.item} defaultType={txDialog.type} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete item?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{toDelete?.name}</strong> from your inventory. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
